import { z } from "zod";
import { useContext } from "react";
import { createWorksite, getWorksite, saveWorksite } from "../api/worksite-controller";
import { ModelDefinition, ValidationMessage, validationMessage } from "../base-components/hooks/useModel";
import { Context } from "../App";
import { RoutePath } from "@/generic/config";
import { CustomerHitSchema, emptyCustomerHitObject } from "./customer-hit-schema";
import { AttachedFileSchema } from "./attached-file.schema";
import { PropertyValueSchema } from "./property";

export const WorksiteSchema = z.object({
    id: z.number(),
    externalId: z.string().nullable(),
    active: z.boolean(),
    customer: CustomerHitSchema.nullable(),
    identifier: z.string().nullable(),
    name: z.string(),
    address: z.string(),
    postalCode: z.string(),
    city: z.string(),
    notes: z.string(),
    contactPerson: z.string(),
    contactEmail: z.string().email().optional(),
    contactPhone: z.string(),
    latitude: z.number(),
    longitude: z.number(),
    extraGeoData: z.string(),
    attachedFiles: z.array(AttachedFileSchema),
    propertyValues: z.array(PropertyValueSchema),
});

type Worksite = z.infer<typeof WorksiteSchema>;

export default Worksite;

const baseWorksiteHitSchema = {
    id: z.number(),
    externalId: z.string().nullable(),
    active: z.boolean(),
    name: z.string(),
    city: z.string(),
    address: z.string(),
    postalCode: z.string(),
    contactPhone: z.string(),
};

export const WorksiteHitSchemaWithoutCustomer = z.object({
    ...baseWorksiteHitSchema,
});

export const WorksiteHitSchema = z.object({
    ...baseWorksiteHitSchema,
    customerHit: CustomerHitSchema.nullable(),
});

export type WorksiteHitWithoutCustomer = z.infer<typeof WorksiteHitSchemaWithoutCustomer>;
export type WorksiteHit = z.infer<typeof WorksiteHitSchema>;

const ValidationMessages = (model: Worksite) => {
    const { translations } = useContext(Context);
    const rules = translations.model.worksite.validationRules;
    const messages: ValidationMessage<Worksite>[] = [];

    if (!model.externalId || model.externalId.trim() === "") {
        messages.push(validationMessage("externalId", rules.externalIdIsRequired, "error"));
    }

    if (!model.customer || !model.customer.id) {
        messages.push(validationMessage("customer", rules.customerIsRequired, "error"));
    }

    if (!model.address) {
        messages.push(validationMessage("address", rules.addressIsRequired, "error"));
    }

    if (!model.city) {
        messages.push(validationMessage("city", rules.cityIsRequired, "error"));
    }

    if (!model.postalCode) {
        messages.push(validationMessage("postalCode", rules.postalCodeIsRequired, "error"));
    }


    return messages;
};

export const emptyWorksiteObject: Worksite = {
    id: 0,
    externalId: "",
    active: true,
    customer: CustomerHitSchema.parse(emptyCustomerHitObject),
    identifier: "",
    name: "",
    address: "",
    postalCode: "",
    city: "",
    notes: "",
    contactPerson: "",
    contactPhone: "",
    latitude: 0,
    longitude: 0,
    extraGeoData: "",
    attachedFiles: [],
    propertyValues: [],
};

export const emptyWorksiteHitObject: WorksiteHit = { // TODO: should this be actual hit or is this OK
    ...emptyWorksiteObject,
    customerHit: null,
};

export const WorksiteModel: ModelDefinition<Worksite> = {
    getById: getWorksite,
    saveFromModel: saveWorksite,
    createFromModel: createWorksite,
    validationMessages: ValidationMessages,
    routePath: RoutePath.Worksites,
    typeProviderObject: WorksiteSchema.parse(emptyWorksiteObject),
};

export const getWorksiteAddress = (worksite: Worksite | WorksiteHit) => {
    return worksite.address + ", " + worksite.postalCode + " " + worksite.city;
};

export const getWorksiteFullName = (worksite: WorksiteHit | Worksite) => {
    return `${worksite.city}, ${worksite.postalCode}, ${worksite.address} ${worksite.externalId ? `(${worksite.externalId})` : ""}`;
};